import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, ReferenceLine,
} from "recharts";
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useLiveMetrics, HistoryPoint } from "../hooks/useLiveMetrics";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Summary {
  active_meters: number; total_meters: number;
  total_balance_units: number; monthly_revenue_kes: number; month: string;
}
interface PaymentTrend { month: string; total: number; count: number; }
interface MeterObj {
  id: number; serial_number: string; unit_number: string; property_name: string;
  status: "online" | "offline" | "fault"; valve_open: boolean;
  last_seen: string | null; firmware_version: string | null;
  mqtt_topic: string; installed_at: string | null;
  latest_reading: {
    flow_rate: string; cumulative_value: string;
    pressure_bar: string; battery_percent: number;
  } | null;
}
interface TokenBalance { meter: number; serial_number: string; balance: string; unit_label: string; }
interface Payment {
  id: number; amount: string; phone_number: string; status: string;
  token_code: string | null; units_allocated: string | null; created_at: string;
}
interface DailyUsage { date: string; units_consumed: string; }
interface TenantProfile {
  meter_id: number | null; meter_serial: string | null;
  meter_status: "online" | "offline" | "fault" | null; valve_open: boolean;
  unit_number: string | null; property_name: string | null;
  balance_units: number; total_paid: number; last_payment: string | null;
}
interface DbReading {
  id: number; cumulative_value: string; flow_rate: string;
  pressure_bar: string; battery_percent: number; timestamp: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n: number, d = 1) =>
  new Intl.NumberFormat("en-KE", { maximumFractionDigits: d }).format(n);
function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}
function timeAgo(iso: string | null): string {
  if (!iso) return "Never";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)  return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gradient-to-r from-gray-100 to-gray-50 rounded-2xl ${className}`} />;
}

// ─── Chart tooltip ────────────────────────────────────────────────────────────
function ChartTip({ active, payload, label, prefix = "", suffix = "" }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0b1f4a] border border-white/10 text-white text-[11.5px] px-3.5 py-2.5 rounded-xl shadow-2xl">
      <p className="text-sky-300/70 font-bold mb-1.5 text-[11px]">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-white/60">{p.name}:</span>
          <span className="font-black text-white">
            {prefix}{typeof p.value === "number" ? fmt(p.value, 3) : p.value}{suffix}
          </span>
        </p>
      ))}
    </div>
  );
}

// ─── Gradient KPI card ────────────────────────────────────────────────────────
function KpiCard({
  label, value, sub, icon, gradient, badge, pulse = false, trend,
}: {
  label: string; value: string; sub?: string; icon: React.ReactNode;
  gradient: string; badge?: string; pulse?: boolean;
  trend?: { dir: "up" | "down" | "flat"; label: string };
}) {
  return (
    <div className={`relative overflow-hidden rounded-2xl p-5 shadow-lg ${gradient}`}>
      <div className="pointer-events-none absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-24 h-24 rounded-full bg-black/10 blur-2xl" />
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-inner">
            {icon}
          </div>
          {badge && (
            <span className="text-[10px] font-black px-2 py-1 rounded-full bg-black/20 text-white flex items-center gap-1">
              {pulse && <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />}
              {badge}
            </span>
          )}
        </div>
        <p className="text-[11px] font-bold text-white/60 uppercase tracking-widest mb-0.5">{label}</p>
        <p className="text-[26px] font-black text-white leading-none">{value}</p>
        {sub && <p className="text-[11.5px] text-white/50 mt-1.5">{sub}</p>}
        {trend && (
          <div className={`flex items-center gap-1 mt-2 ${trend.dir === "up" ? "text-emerald-300" : trend.dir === "down" ? "text-rose-300" : "text-white/40"}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-3 h-3">
              {trend.dir === "up" ? <path d="M18 15l-6-6-6 6" strokeLinecap="round" strokeLinejoin="round"/> :
               trend.dir === "down" ? <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/> :
               <path d="M5 12h14" strokeLinecap="round"/>}
            </svg>
            <span className="text-[10.5px] font-bold">{trend.label}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Card wrapper ─────────────────────────────────────────────────────────────
function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${className}`}>
      {children}
    </div>
  );
}
function CardHeader({ title, sub, action }: { title: string; sub?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between mb-4 p-5 pb-0">
      <div>
        <h3 className="text-[14.5px] font-black text-gray-800">{title}</h3>
        {sub && <p className="text-[11.5px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Accordion ────────────────────────────────────────────────────────────────
function Accordion({
  title, subtitle, icon, defaultOpen = false, badge, children,
}: {
  title: string; subtitle?: string; icon: React.ReactNode;
  defaultOpen?: boolean; badge?: string; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/60 transition-colors text-left"
      >
        <div className="flex items-center gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-50 to-indigo-50 border border-sky-100/80 flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[14px] font-black text-gray-800">{title}</h3>
              {badge && (
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-100">
                  {badge}
                </span>
              )}
            </div>
            {subtitle && <p className="text-[11.5px] text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <div className={`w-8 h-8 rounded-xl bg-gray-100/80 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth={2.5} className="w-3.5 h-3.5">
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </button>
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${open ? "max-h-[3000px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="border-t border-gray-100">{children}</div>
      </div>
    </div>
  );
}

// ─── Alert banner ─────────────────────────────────────────────────────────────
function AlertBanner({
  level, message, onDismiss,
}: {
  level: "info" | "warning" | "critical"; message: string; onDismiss: () => void;
}) {
  const cfg = {
    critical: { bg: "bg-rose-50 border-rose-200", icon: "text-rose-500", text: "text-rose-800", dot: "bg-rose-500" },
    warning:  { bg: "bg-amber-50 border-amber-200", icon: "text-amber-500", text: "text-amber-800", dot: "bg-amber-500" },
    info:     { bg: "bg-sky-50 border-sky-200", icon: "text-sky-500", text: "text-sky-800", dot: "bg-sky-400" },
  }[level];
  return (
    <div className={`flex items-start gap-3 px-4 py-3.5 rounded-2xl border ${cfg.bg}`}>
      <div className="flex-shrink-0 mt-0.5">
        <span className={`w-2 h-2 rounded-full ${cfg.dot} inline-block`} />
      </div>
      <p className={`flex-1 text-[13px] font-semibold ${cfg.text}`}>{message}</p>
      <button onClick={onDismiss} className={`flex-shrink-0 ${cfg.icon} hover:opacity-70 transition-opacity`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
          <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

// ─── Balance ring ─────────────────────────────────────────────────────────────
function BalanceRing({ balance, max = 100 }: { balance: number; max?: number }) {
  const pct = Math.min(100, (balance / max) * 100);
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  const color = balance < 5 ? "#f43f5e" : balance < 20 ? "#f59e0b" : "#10b981";
  const trackColor = balance < 5 ? "#fee2e2" : balance < 20 ? "#fef3c7" : "#d1fae5";
  return (
    <div className="relative w-40 h-40 flex items-center justify-center mx-auto">
      <svg width="160" height="160" className="absolute inset-0 -rotate-90" style={{ overflow: "visible" }}>
        {/* Track */}
        <circle cx="80" cy="80" r={r} fill="none" stroke={trackColor} strokeWidth="11" />
        {/* Progress */}
        <circle cx="80" cy="80" r={r} fill="none" stroke={color} strokeWidth="11"
          strokeLinecap="round"
          strokeDasharray={`${circ}`}
          strokeDashoffset={`${offset}`}
          style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)", filter: `drop-shadow(0 0 6px ${color}55)` }}
        />
      </svg>
      <div className="relative z-10 text-center">
        <p className="text-[30px] font-black leading-none" style={{ color }}>{fmt(balance, 1)}</p>
        <p className="text-[11px] font-bold text-gray-400 mt-1">units</p>
        <p className="text-[10px] font-semibold text-gray-300 mt-0.5">{fmt(pct, 0)}% full</p>
      </div>
    </div>
  );
}

// ─── Meter status badge ───────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string | null }) {
  const cfg = {
    online:  "bg-emerald-100 text-emerald-700 border-emerald-200",
    offline: "bg-gray-100 text-gray-500 border-gray-200",
    fault:   "bg-rose-100 text-rose-600 border-rose-200",
  }[status ?? "offline"] ?? "bg-gray-100 text-gray-500 border-gray-200";
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-black px-2.5 py-1 rounded-full border capitalize ${cfg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === "online" ? "bg-emerald-500 animate-pulse" : status === "fault" ? "bg-rose-500" : "bg-gray-400"}`} />
      {status ?? "Unknown"}
    </span>
  );
}

// ─── Info row (for meter details grid) ───────────────────────────────────────
function InfoItem({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-[10.5px] font-black text-gray-400 uppercase tracking-widest">{label}</p>
      <p className={`text-[13px] font-bold text-gray-800 ${mono ? "font-mono text-[12px]" : ""}`}>{value || "—"}</p>
    </div>
  );
}

// ─── Timeframe selector ───────────────────────────────────────────────────────
function TimeframeTabs({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
      {[7, 30, 60].map((d) => (
        <button
          key={d}
          onClick={() => onChange(d)}
          className={`px-3 py-1 rounded-lg text-[11.5px] font-black transition-all ${value === d ? "bg-white text-[#0b1f4a] shadow-sm" : "text-gray-400 hover:text-gray-600"}`}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  TENANT DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
function TenantDashboard() {
  const { user } = useAuthStore();
  const [meter, setMeter]               = useState<MeterObj | null>(null);
  const [meterId, setMeterId]           = useState<number | null>(null);
  const [profile, setProfile]           = useState<TenantProfile | null>(null);
  const [recentPayments, setRecentPayments] = useState<Payment[]>([]);
  const [usageData, setUsageData]       = useState<DailyUsage[]>([]);
  const [dbReadings, setDbReadings]     = useState<DbReading[]>([]);
  const [usageDays, setUsageDays]       = useState(30);
  const [loading, setLoading]           = useState(true);
  const [alerts, setAlerts]             = useState<Array<{ id: string; level: "info"|"warning"|"critical"; message: string }>>([]);
  const [allPayments, setAllPayments]   = useState<Payment[]>([]);

  const dismissAlert = useCallback((id: string) => {
    setAlerts((a) => a.filter((x) => x.id !== id));
  }, []);

  // Fetch usage when timeframe changes
  useEffect(() => {
    if (!meterId) return;
    api.get(`/analytics/meter/${meterId}/usage/?days=${usageDays}`)
      .then(({ data }) => setUsageData(data))
      .catch(() => {});
  }, [meterId, usageDays]);

  useEffect(() => {
    // 1. Meter first (fastest path to WebSocket)
    api.get("/meters/my/")
      .then(({ data }: { data: MeterObj }) => {
        setMeter(data);
        setMeterId(data.id);
        // Seed alert system
        const newAlerts: typeof alerts = [];
        if (data.status === "fault")
          newAlerts.push({ id: "fault", level: "critical", message: "⚠ Meter fault detected. Please contact your property manager immediately." });
        else if (data.status === "offline")
          newAlerts.push({ id: "offline", level: "warning", message: "Your meter appears to be offline. Last sync: " + (data.last_seen ? new Date(data.last_seen).toLocaleString("en-KE") : "never") });
        if (!data.valve_open)
          newAlerts.push({ id: "valve", level: "info", message: "💧 Your water valve is currently closed. Contact your manager if this is unexpected." });
        setAlerts(newAlerts);

        // Fetch telemetry history + usage
        api.get(`/meters/${data.id}/readings/?limit=100`)
          .then(({ data: r }) => setDbReadings((r.results ?? r).reverse()))
          .catch(() => {});
        api.get(`/analytics/meter/${data.id}/usage/?days=30`)
          .then(({ data: ud }) => setUsageData(ud))
          .catch(() => {});
      })
      .catch(() => {});

    // 2. Profile + payments in parallel
    Promise.all([
      api.get("/auth/tenants/me/").catch(() => ({ data: null })),
      api.get("/payments/history/").catch(() => ({ data: [] })),
    ]).then(([p, pay]) => {
      if (p.data?.meter_id) {
        setMeterId(p.data.meter_id);
        setProfile(p.data);
        const bal = p.data.balance_units ?? 0;
        if (bal < 5 && !alerts.find((a) => a.id === "balance_critical"))
          setAlerts((prev) => [
            { id: "balance_critical", level: "critical", message: `🚨 Critical low balance! Only ${fmt(bal, 1)} units remaining. Top up now to avoid disconnection.` },
            ...prev,
          ]);
        else if (bal < 20 && !alerts.find((a) => a.id === "balance_low"))
          setAlerts((prev) => [
            { id: "balance_low", level: "warning", message: `Low balance: ${fmt(bal, 1)} units. Consider topping up soon.` },
            ...prev,
          ]);
      }
      const pays = pay.data.results ?? pay.data ?? [];
      setRecentPayments(pays.slice(0, 5));
      setAllPayments(pays);
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const { reading, history, connected } = useLiveMetrics(meterId);

  // Merge DB + live history
  const mergedHistory: HistoryPoint[] = useMemo(() => {
    const dbPoints: HistoryPoint[] = dbReadings.map((r) => ({
      time: new Date(r.timestamp).toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" }),
      cumulative: parseFloat(r.cumulative_value),
      flow_rate:  parseFloat(r.flow_rate),
      pressure:   parseFloat(r.pressure_bar),
      timestamp:  r.timestamp,
    }));
    const liveKeys = new Set(history.map((h) => h.timestamp));
    const base = dbPoints.filter((d) => !liveKeys.has(d.timestamp));
    return [...base, ...history].slice(-80);
  }, [dbReadings, history]);

  // Live values with DB fallback
  const lastDb = dbReadings[dbReadings.length - 1] ?? null;
  const liveFlow     = reading ? parseFloat(reading.flow_rate)  : (lastDb ? parseFloat(lastDb.flow_rate) : null);
  const livePressure = reading ? parseFloat(reading.pressure)   : (lastDb ? parseFloat(lastDb.pressure_bar) : null);
  const liveCumul    = reading ? parseFloat(reading.cumulative) : (lastDb ? parseFloat(lastDb.cumulative_value) : null);
  const liveBattery  = lastDb?.battery_percent ?? null;
  const liveValve    = reading ? reading.valve_open : (meter?.valve_open ?? false);

  const balance  = profile?.balance_units ?? 0;
  const balPct   = Math.min(100, (balance / 100) * 100);
  const balState = balance < 5 ? "critical" : balance < 20 ? "low" : "healthy";
  const balConfig = {
    critical: { bar: "from-rose-400 to-red-500",    text: "text-rose-500",    label: "Critical",  ring: "ring-rose-400/30"    },
    low:      { bar: "from-amber-400 to-orange-500", text: "text-amber-500",  label: "Low",       ring: "ring-amber-400/30"   },
    healthy:  { bar: "from-emerald-400 to-teal-500", text: "text-emerald-500", label: "Healthy",  ring: "ring-emerald-400/30" },
  }[balState];

  // 30-day stats
  const usageArea = usageData.map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-KE", { day: "numeric", month: "short" }),
    consumed: parseFloat(d.units_consumed),
  }));
  const totalConsumed = usageArea.reduce((s, d) => s + d.consumed, 0);
  const avgDaily      = usageArea.length ? totalConsumed / usageArea.length : 0;
  const peakDay       = usageArea.reduce((m, d) => d.consumed > m.consumed ? d : m, { date: "—", consumed: 0 });
  const daysRemaining = avgDaily > 0 ? Math.floor(balance / avgDaily) : null;

  // Payment status colours
  const payColors: Record<string, string> = {
    completed: "text-emerald-700 bg-emerald-50 border-emerald-100",
    pending:   "text-amber-700 bg-amber-50 border-amber-100",
    failed:    "text-rose-700 bg-rose-50 border-rose-100",
    cancelled: "text-gray-500 bg-gray-100 border-gray-200",
  };

  const now     = new Date();
  const dateStr = now.toLocaleDateString("en-KE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  if (loading && !meter) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-52" />
        <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-16" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4"><Skeleton className="h-56" /><Skeleton className="h-56" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-12">

      {/* ── Alert banners ── */}
      {alerts.length > 0 && (
        <div className="space-y-2.5">
          {alerts.map((a) => (
            <AlertBanner key={a.id} level={a.level} message={a.message} onDismiss={() => dismissAlert(a.id)} />
          ))}
        </div>
      )}

      {/* ── Greeting row ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-black text-gray-900">
            {greeting()}, {user?.first_name || user?.username} 👋
          </h1>
          <p className="text-[12.5px] text-gray-400 mt-0.5">{dateStr}</p>
        </div>
        <div className={`inline-flex items-center gap-2.5 px-4 py-2.5 rounded-2xl border shadow-sm transition-all ${connected ? "bg-emerald-50 border-emerald-100" : "bg-gray-50 border-gray-100"}`}>
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${connected ? "bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" : "bg-gray-300"}`} />
          <span className={`text-[12.5px] font-black ${connected ? "text-emerald-700" : "text-gray-400"}`}>
            {connected ? "Live Telemetry Active" : "Awaiting signal…"}
          </span>
          {connected && <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping" />}
        </div>
      </div>

      {/* ── Hero banner ── */}
      {meter ? (
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#07183a] via-[#0d2660] to-[#152f7a] shadow-2xl">
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-sky-400/10 blur-3xl" />
            <div className="absolute top-1/2 left-1/3 w-48 h-48 rounded-full bg-indigo-400/5 blur-2xl" />
            {/* Dot grid */}
            <svg className="absolute inset-0 w-full h-full opacity-[0.045]" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="dots" width="28" height="28" patternUnits="userSpaceOnUse">
                  <circle cx="1.5" cy="1.5" r="1.5" fill="white" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dots)" />
            </svg>
          </div>

          <div className="relative z-10 p-7">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8">

              {/* Left: property + meter info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-black text-sky-400/60 uppercase tracking-[0.25em]">Smart Meter</span>
                  {connected && (
                    <span className="flex items-center gap-1 text-[9.5px] font-black text-emerald-300 bg-emerald-400/15 border border-emerald-400/25 px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping" />
                      LIVE
                    </span>
                  )}
                </div>
                <h2 className="text-[26px] font-black text-white leading-tight mb-1">{meter.property_name}</h2>
                <p className="text-sky-300/60 text-[13.5px]">
                  Unit <span className="text-sky-200 font-black">{meter.unit_number}</span>
                  <span className="mx-2 text-white/20">·</span>
                  <span className="font-mono text-sky-300/50 text-[12px]">{meter.serial_number}</span>
                </p>

                {/* Status chips row */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {/* Meter status */}
                  <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/8 border border-white/10 backdrop-blur-sm">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${meter.status === "online" ? "bg-emerald-400 animate-pulse" : meter.status === "fault" ? "bg-rose-400" : "bg-gray-400"}`} />
                    <span className="text-[12px] font-black text-white capitalize">{meter.status}</span>
                  </div>
                  {/* Valve */}
                  <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/8 border border-white/10 backdrop-blur-sm">
                    <svg viewBox="0 0 24 24" fill="none" strokeWidth={2} stroke={liveValve ? "#38bdf8" : "#f87171"} className="w-3.5 h-3.5">
                      <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" />
                    </svg>
                    <span className="text-[12px] font-black text-white">Valve {liveValve ? "Open" : "Closed"}</span>
                  </div>
                  {/* Last seen */}
                  <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/8 border border-white/10 backdrop-blur-sm">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth={1.8} className="w-3.5 h-3.5">
                      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" strokeLinecap="round" />
                    </svg>
                    <span className="text-[12px] font-black text-white/60">{timeAgo(meter.last_seen)}</span>
                  </div>
                  {/* Battery */}
                  {liveBattery !== null && (
                    <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/8 border border-white/10 backdrop-blur-sm">
                      <svg viewBox="0 0 24 24" fill="none" stroke={liveBattery < 20 ? "#f87171" : "#6ee7b7"} strokeWidth={1.8} className="w-3.5 h-3.5">
                        <rect x="2" y="7" width="18" height="10" rx="2" /><path d="M22 11v2" strokeLinecap="round" strokeWidth={2.5} />
                        <rect x="3.5" y="8.5" width={`${(liveBattery / 100) * 13}`} height="7" rx="1" fill={liveBattery < 20 ? "#f87171" : "#6ee7b7"} stroke="none" />
                      </svg>
                      <span className={`text-[12px] font-black ${liveBattery < 20 ? "text-rose-300" : "text-emerald-300"}`}>{liveBattery}%</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Center: Balance ring */}
              <div className="flex flex-col items-center">
                <BalanceRing balance={balance} max={100} />
                <div className="mt-3 text-center">
                  <p className={`text-[11.5px] font-black uppercase tracking-wider ${balConfig.text.replace("text-", "text-").replace("500", "400")}`}>
                    {balConfig.label} Balance
                  </p>
                  {daysRemaining !== null && (
                    <p className="text-[11px] text-white/30 mt-0.5">~{daysRemaining} days remaining</p>
                  )}
                </div>
              </div>

              {/* Right: live KPI strip */}
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2.5 min-w-[140px]">
                {[
                  { label: "Flow Rate",  value: liveFlow !== null ? `${fmt(liveFlow, 3)} L/min` : "—", color: "text-sky-300" },
                  { label: "Pressure",   value: livePressure !== null ? `${fmt(livePressure, 2)} bar` : "—", color: "text-violet-300" },
                  { label: "Cumulative", value: liveCumul !== null ? `${fmt(liveCumul, 0)} L` : "—", color: "text-teal-300" },
                ].map((s) => (
                  <div key={s.label} className="px-4 py-3 rounded-xl bg-white/8 border border-white/10 backdrop-blur-sm">
                    <p className="text-[9.5px] font-black text-white/40 uppercase tracking-widest mb-0.5">{s.label}</p>
                    <p className={`text-[15px] font-black ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Balance progress bar */}
            <div className="mt-6 pt-5 border-t border-white/8">
              <div className="flex justify-between text-[11px] text-white/35 mb-2">
                <span>Token Balance Meter</span>
                <span className={`font-black ${balConfig.text.replace("500", "300")}`}>{fmt(balPct, 0)}% · {fmt(balance, 1)} units left</span>
              </div>
              <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${balConfig.bar} transition-all duration-1000`}
                  style={{ width: `${balPct}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-3xl border-2 border-dashed border-gray-200 bg-gray-50 p-14 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-sky-50 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.4} className="w-8 h-8">
              <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" />
            </svg>
          </div>
          <p className="text-[15px] font-black text-gray-700">No Meter Assigned</p>
          <p className="text-[13px] text-gray-400 mt-1.5 max-w-xs mx-auto">Contact your property manager to assign a smart meter to your unit.</p>
        </div>
      )}

      {/* ── 5 KPI cards ── */}
      {meterId && (
        <div className="grid grid-cols-2 xl:grid-cols-5 gap-3.5">
          <KpiCard
            label="Flow Rate"
            value={liveFlow !== null ? fmt(liveFlow, 3) : "—"}
            sub="Litres per minute"
            badge={connected ? "LIVE" : undefined}
            pulse={connected}
            gradient="bg-gradient-to-br from-sky-500 to-blue-600"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8} className="w-5 h-5"><path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>}
          />
          <KpiCard
            label="Pressure"
            value={livePressure !== null ? `${fmt(livePressure, 2)}` : "—"}
            sub="Bar (water pressure)"
            gradient="bg-gradient-to-br from-violet-500 to-purple-700"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8} className="w-5 h-5"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" strokeLinecap="round" /></svg>}
          />
          <KpiCard
            label="Total Consumed"
            value={liveCumul !== null ? `${fmt(liveCumul, 0)}` : "—"}
            sub="Litres (all time)"
            gradient="bg-gradient-to-br from-teal-500 to-emerald-600"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8} className="w-5 h-5"><path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" /></svg>}
          />
          <KpiCard
            label="Battery"
            value={liveBattery !== null ? `${liveBattery}%` : "—"}
            sub="Device battery"
            gradient={`bg-gradient-to-br ${liveBattery !== null && liveBattery < 20 ? "from-rose-500 to-red-600" : "from-slate-600 to-slate-800"}`}
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8} className="w-5 h-5"><rect x="2" y="7" width="18" height="10" rx="2" /><path d="M22 11v2" strokeLinecap="round" strokeWidth={2.5} /></svg>}
          />
          <KpiCard
            label="Token Balance"
            value={fmt(balance, 1)}
            sub={`${balConfig.label} · ${daysRemaining !== null ? `~${daysRemaining}d` : "calc..."} left`}
            gradient={`bg-gradient-to-br ${balState === "critical" ? "from-rose-500 to-red-600" : balState === "low" ? "from-amber-500 to-orange-600" : "from-emerald-500 to-green-600"}`}
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8} className="w-5 h-5"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeLinecap="round" /></svg>}
          />
        </div>
      )}

      {/* ── Accordion: Meter Information ── */}
      {meter && (
        <Accordion
          title="Meter Information"
          subtitle={`Serial: ${meter.serial_number} · Installed ${meter.installed_at ? new Date(meter.installed_at).toLocaleDateString("en-KE", { day: "numeric", month: "long", year: "numeric" }) : "Unknown"}`}
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-5 h-5"><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M9 7h6M9 11h6M9 15h4" strokeLinecap="round" /></svg>}
          badge="Device"
        >
          <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-5">
            <InfoItem label="Serial Number" value={meter.serial_number} mono />
            <InfoItem label="Property" value={meter.property_name} />
            <InfoItem label="Unit" value={meter.unit_number} />
            <div className="flex flex-col gap-1">
              <p className="text-[10.5px] font-black text-gray-400 uppercase tracking-widest">Status</p>
              <StatusBadge status={meter.status} />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-[10.5px] font-black text-gray-400 uppercase tracking-widest">Valve</p>
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-black px-2.5 py-1 rounded-full border w-fit ${liveValve ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-rose-50 text-rose-600 border-rose-200"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${liveValve ? "bg-sky-500" : "bg-rose-500"}`} />
                {liveValve ? "Open (Flowing)" : "Closed"}
              </span>
            </div>
            <InfoItem label="Last Seen" value={meter.last_seen ? new Date(meter.last_seen).toLocaleString("en-KE") : "Never"} />
            <InfoItem label="Firmware" value={meter.firmware_version ?? "Unknown"} mono />
            <InfoItem label="MQTT Topic" value={meter.mqtt_topic} mono />
            <InfoItem label="Installed" value={meter.installed_at ? new Date(meter.installed_at).toLocaleDateString("en-KE", { day: "numeric", month: "long", year: "numeric" }) : "Unknown"} />
            {liveBattery !== null && (
              <div className="flex flex-col gap-1 col-span-2">
                <p className="text-[10.5px] font-black text-gray-400 uppercase tracking-widest">Battery Level</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${liveBattery < 20 ? "bg-gradient-to-r from-rose-400 to-red-500" : liveBattery < 50 ? "bg-gradient-to-r from-amber-400 to-orange-400" : "bg-gradient-to-r from-emerald-400 to-green-500"}`}
                      style={{ width: `${liveBattery}%` }}
                    />
                  </div>
                  <span className="text-[12.5px] font-black text-gray-700">{liveBattery}%</span>
                </div>
              </div>
            )}
          </div>
        </Accordion>
      )}

      {/* ── Live telemetry charts ── */}
      {meterId && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Flow rate */}
          <Card>
            <CardHeader
              title="Flow Rate"
              sub={`${mergedHistory.length} readings · ${connected ? "updating live" : "last saved snapshot"}`}
              action={<span className={`text-[11px] font-black px-2.5 py-1 rounded-lg ${connected ? "text-sky-600 bg-sky-50" : "text-gray-400 bg-gray-100"}`}>L/min</span>}
            />
            <div className="px-5 pb-5">
              {mergedHistory.length === 0 ? (
                <div className="h-44 flex items-center justify-center text-gray-300 text-sm">No readings yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={mergedHistory} margin={{ top: 4, right: 2, left: -22, bottom: 0 }}>
                    <defs>
                      <linearGradient id="flowG" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#0ea5e9" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#cbd5e1" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10, fill: "#cbd5e1" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTip suffix=" L/min" />} />
                    <Area type="monotone" dataKey="flow_rate" name="Flow" stroke="#0ea5e9" strokeWidth={2.5}
                      fill="url(#flowG)" dot={false} isAnimationActive={false}
                      activeDot={{ r: 5, fill: "#0ea5e9", stroke: "#fff", strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>

          {/* Pressure */}
          <Card>
            <CardHeader
              title="Pressure"
              sub={`${mergedHistory.length} readings · ${connected ? "updating live" : "last saved snapshot"}`}
              action={<span className="text-[11px] font-black px-2.5 py-1 rounded-lg text-violet-600 bg-violet-50">bar</span>}
            />
            <div className="px-5 pb-5">
              {mergedHistory.length === 0 ? (
                <div className="h-44 flex items-center justify-center text-gray-300 text-sm">No readings yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={mergedHistory} margin={{ top: 4, right: 2, left: -22, bottom: 0 }}>
                    <defs>
                      <linearGradient id="presG" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#7c3aed" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#cbd5e1" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10, fill: "#cbd5e1" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTip suffix=" bar" />} />
                    <Line type="monotone" dataKey="pressure" name="Pressure" stroke="#7c3aed" strokeWidth={2.5}
                      dot={false} isAnimationActive={false}
                      activeDot={{ r: 5, fill: "#7c3aed", stroke: "#fff", strokeWidth: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* ── Cumulative usage trend ── */}
      {meterId && mergedHistory.length > 0 && (
        <Card>
          <CardHeader
            title="Cumulative Consumption Trend"
            sub={`Total litres consumed over time · ${mergedHistory.length} data points`}
            action={<span className="text-[11px] font-black px-2.5 py-1 rounded-lg text-teal-600 bg-teal-50">Litres</span>}
          />
          <div className="px-5 pb-5">
            <ResponsiveContainer width="100%" height={175}>
              <AreaChart data={mergedHistory} margin={{ top: 4, right: 2, left: -5, bottom: 0 }}>
                <defs>
                  <linearGradient id="cumulG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#14b8a6" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#cbd5e1" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "#cbd5e1" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${fmt(v, 0)}`} />
                <Tooltip content={<ChartTip suffix=" L" />} />
                <Area type="monotone" dataKey="cumulative" name="Cumulative" stroke="#14b8a6" strokeWidth={2.5}
                  fill="url(#cumulG)" dot={false} isAnimationActive={false}
                  activeDot={{ r: 5, fill: "#14b8a6", stroke: "#fff", strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* ── Water balance deep dive ── */}
      {profile && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Balance card */}
          <Card className="lg:col-span-1 flex flex-col items-center justify-center p-6 text-center">
            <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-3">Current Balance</p>
            <BalanceRing balance={balance} max={100} />
            <div className="mt-4 w-full">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-[12px] font-black ${balState === "critical" ? "bg-rose-50 border-rose-200 text-rose-700" : balState === "low" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                <span className={`w-2 h-2 rounded-full ${balState === "critical" ? "bg-rose-500" : balState === "low" ? "bg-amber-500" : "bg-emerald-500"}`} />
                {balConfig.label} — {fmt(balance, 1)} units
              </div>
            </div>
            {balState !== "healthy" && (
              <a href="/payments" className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-white text-[13px] font-black shadow-lg shadow-sky-500/30 hover:shadow-sky-500/50 transition-all hover:-translate-y-0.5">
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} className="w-4 h-4"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeLinecap="round" /></svg>
                Top Up Now
              </a>
            )}
          </Card>

          {/* Usage stats */}
          <Card className="lg:col-span-2 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-[14.5px] font-black text-gray-800">Consumption Overview</h3>
                <p className="text-[11.5px] text-gray-400 mt-0.5">Last {usageDays} days of water usage</p>
              </div>
              <TimeframeTabs value={usageDays} onChange={setUsageDays} />
            </div>

            {/* Stat chips */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: "Total",     value: `${fmt(totalConsumed, 1)} L`, color: "bg-sky-50 border-sky-100 text-sky-700" },
                { label: "Daily Avg", value: `${fmt(avgDaily, 1)} L`,      color: "bg-violet-50 border-violet-100 text-violet-700" },
                { label: "Peak Day",  value: `${fmt(peakDay.consumed, 1)} L`, color: "bg-teal-50 border-teal-100 text-teal-700" },
              ].map((s) => (
                <div key={s.label} className={`rounded-xl border px-3 py-2.5 text-center ${s.color}`}>
                  <p className="text-[10px] font-black uppercase tracking-wider opacity-60 mb-0.5">{s.label}</p>
                  <p className="text-[16px] font-black">{s.value}</p>
                </div>
              ))}
            </div>

            {usageArea.length === 0 ? (
              <div className="h-36 flex items-center justify-center text-gray-300 text-sm">No usage data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={155}>
                <AreaChart data={usageArea} margin={{ top: 4, right: 2, left: -22, bottom: 0 }}>
                  <defs>
                    <linearGradient id="usageG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#cbd5e1" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: "#cbd5e1" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip suffix=" L" />} />
                  {avgDaily > 0 && <ReferenceLine y={avgDaily} stroke="#0ea5e9" strokeDasharray="4 4" strokeOpacity={0.5} label={{ value: "Avg", fontSize: 10, fill: "#0ea5e9" }} />}
                  <Area type="monotone" dataKey="consumed" name="Consumed" stroke="#0ea5e9" strokeWidth={2.5}
                    fill="url(#usageG)" dot={false}
                    activeDot={{ r: 4, fill: "#0ea5e9", stroke: "#fff", strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            )}

            {/* Days remaining estimate */}
            {daysRemaining !== null && (
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${daysRemaining < 3 ? "bg-rose-50" : daysRemaining < 7 ? "bg-amber-50" : "bg-emerald-50"}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke={daysRemaining < 3 ? "#f43f5e" : daysRemaining < 7 ? "#f59e0b" : "#10b981"} strokeWidth={1.8} className="w-5 h-5">
                    <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <p className="text-[12.5px] font-black text-gray-800">
                    Estimated <span className={daysRemaining < 3 ? "text-rose-600" : daysRemaining < 7 ? "text-amber-600" : "text-emerald-600"}>{daysRemaining} days</span> of water remaining
                  </p>
                  <p className="text-[11px] text-gray-400">Based on {fmt(avgDaily, 1)} L/day average · {fmt(balance, 1)} units balance</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ── Accordion: Recent payments ── */}
      <Accordion
        title="Payment History"
        subtitle={`${allPayments.length} transaction${allPayments.length !== 1 ? "s" : ""} · Total spend KES ${fmt(profile?.total_paid ?? 0, 0)}`}
        icon={<svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-5 h-5"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" strokeLinecap="round" /></svg>}
        badge={`${allPayments.length}`}
        defaultOpen={false}
      >
        <div className="p-5 space-y-2.5">
          {allPayments.length === 0 ? (
            <div className="py-10 text-center text-gray-300">
              <svg viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth={1.5} className="w-10 h-10 mx-auto mb-3">
                <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" strokeLinecap="round" />
              </svg>
              <p className="text-[13px]">No payments recorded yet.</p>
            </div>
          ) : (
            <>
              {/* Table header */}
              <div className="hidden sm:grid grid-cols-5 gap-3 px-3 pb-2 border-b border-gray-100">
                {["Date", "Amount", "Phone", "Units", "Status"].map((h) => (
                  <p key={h} className="text-[10.5px] font-black text-gray-400 uppercase tracking-wider">{h}</p>
                ))}
              </div>
              {/* Rows */}
              {allPayments.map((p) => (
                <div key={p.id} className="group flex flex-col sm:grid sm:grid-cols-5 gap-2 sm:gap-3 items-start sm:items-center px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-2 sm:block">
                    <div className={`w-7 h-7 sm:hidden rounded-lg flex items-center justify-center ${p.status === "completed" ? "bg-emerald-50" : "bg-gray-100"}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke={p.status === "completed" ? "#10b981" : "#9ca3af"} strokeWidth={2} className="w-3.5 h-3.5">
                        <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" strokeLinecap="round" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-[12.5px] font-bold text-gray-700">
                        {new Date(p.created_at).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                      <p className="text-[10.5px] text-gray-400 sm:hidden">{p.phone_number}</p>
                    </div>
                  </div>
                  <p className="text-[13px] font-black text-gray-900 sm:col-start-2">KES {parseFloat(p.amount).toLocaleString()}</p>
                  <p className="text-[12px] text-gray-500 hidden sm:block">{p.phone_number}</p>
                  <p className="text-[12px] text-gray-600">
                    {p.units_allocated ? `${fmt(parseFloat(p.units_allocated), 1)} units` : "—"}
                    {p.token_code && <span className="block text-[10px] text-gray-400 font-mono mt-0.5">{p.token_code}</span>}
                  </p>
                  <span className={`text-[10.5px] font-black px-2.5 py-1 rounded-full border capitalize w-fit ${payColors[p.status] ?? "text-gray-500 bg-gray-100 border-gray-200"}`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      </Accordion>

      {/* ── Accordion: Recent Payments summary ── */}
      <Accordion
        title="Recent Transactions"
        subtitle="Last 5 payments at a glance"
        icon={<svg viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth={1.8} className="w-5 h-5"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeLinecap="round" /></svg>}
        defaultOpen={true}
      >
        <div className="p-5 space-y-2">
          {recentPayments.length === 0 ? (
            <p className="text-center text-gray-300 text-sm py-6">No recent payments</p>
          ) : recentPayments.map((p) => (
            <div key={p.id} className="flex items-center gap-3.5 p-3 rounded-xl hover:bg-gray-50 transition-colors">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${p.status === "completed" ? "bg-emerald-50" : "bg-gray-100"}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke={p.status === "completed" ? "#10b981" : "#9ca3af"} strokeWidth={1.8} className="w-4.5 h-4.5">
                  <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" strokeLinecap="round" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-[13px] font-black text-gray-800">KES {parseFloat(p.amount).toLocaleString()}</p>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border capitalize ${payColors[p.status] ?? "text-gray-500 bg-gray-100 border-gray-200"}`}>{p.status}</span>
                </div>
                <p className="text-[11.5px] text-gray-400 mt-0.5">
                  {p.phone_number} · {new Date(p.created_at).toLocaleDateString("en-KE", { day: "numeric", month: "short" })}
                  {p.units_allocated && <span className="ml-2 text-emerald-600 font-semibold">+{fmt(parseFloat(p.units_allocated), 1)} units</span>}
                </p>
              </div>
              {p.token_code && (
                <div className="text-right">
                  <p className="text-[9px] text-gray-400 uppercase tracking-wider">Token</p>
                  <p className="text-[11px] font-mono font-black text-gray-600">{p.token_code}</p>
                </div>
              )}
            </div>
          ))}
          <div className="pt-2">
            <a href="/payments" className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-gray-200 text-[12.5px] font-black text-gray-500 hover:bg-gray-50 transition-colors">
              View All Payments
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </Accordion>

      {/* ── Account summary footer strip ── */}
      {profile && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0b1f4a] to-[#1a3a8a] p-5 shadow-lg">
            <div className="pointer-events-none absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-xl" />
            <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-1">Lifetime Spend</p>
            <p className="text-[28px] font-black text-white leading-none">KES {fmt(profile.total_paid, 0)}</p>
            <p className="text-[11px] text-white/30 mt-1.5">Total water payments</p>
          </div>
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 p-5 shadow-lg">
            <div className="pointer-events-none absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-xl" />
            <p className="text-[10px] font-black text-white/60 uppercase tracking-widest mb-1">Avg Daily Usage</p>
            <p className="text-[28px] font-black text-white leading-none">{avgDaily > 0 ? `${fmt(avgDaily, 1)} L` : "—"}</p>
            <p className="text-[11px] text-white/50 mt-1.5">Over last {usageDays} days</p>
          </div>
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700 p-5 shadow-lg">
            <div className="pointer-events-none absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-xl" />
            <p className="text-[10px] font-black text-white/60 uppercase tracking-widest mb-1">Last Payment</p>
            <p className="text-[16px] font-black text-white leading-tight">
              {profile.last_payment
                ? new Date(profile.last_payment).toLocaleDateString("en-KE", { day: "numeric", month: "long", year: "numeric" })
                : "No payments yet"}
            </p>
            <p className="text-[11px] text-white/50 mt-1.5">{profile.last_payment ? timeAgo(profile.last_payment) : "—"}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MANAGER / ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
function ManagerDashboard() {
  const { user } = useAuthStore();
  const [summary,        setSummary]        = useState<Summary | null>(null);
  const [trends,         setTrends]         = useState<PaymentTrend[]>([]);
  const [meters,         setMeters]         = useState<MeterObj[]>([]);
  const [balances,       setBalances]       = useState<TokenBalance[]>([]);
  const [recentPayments, setRecentPayments] = useState<Payment[]>([]);
  const [usageData,      setUsageData]      = useState<DailyUsage[]>([]);
  const [dbReadings,     setDbReadings]     = useState<DbReading[]>([]);
  const [loading,        setLoading]        = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/dashboard/"),
      api.get("/analytics/payments/trends/"),
      api.get("/meters/"),
      api.get("/tokens/balances/"),
      api.get("/payments/history/"),
    ]).then(([s, t, m, b, p]) => {
      setSummary(s.data);
      setTrends((t.data ?? []).map((d: any) => ({
        ...d,
        month: new Date(d.month).toLocaleDateString("en-KE", { month: "short", year: "2-digit" }),
      })));
      const mList: MeterObj[] = m.data.results ?? m.data;
      setMeters(mList);
      setBalances(b.data.results ?? b.data);
      setRecentPayments((p.data.results ?? p.data).slice(0, 6));
      if (mList.length > 0) {
        api.get(`/analytics/meter/${mList[0].id}/usage/?days=30`)
          .then(({ data }) => setUsageData(data)).catch(() => {});
        api.get(`/meters/${mList[0].id}/readings/?limit=60`)
          .then(({ data }) => setDbReadings((data.results ?? data).reverse())).catch(() => {});
      }
    }).finally(() => setLoading(false));
  }, []);

  const online   = meters.filter((m) => m.status === "online").length;
  const offline  = meters.filter((m) => m.status === "offline").length;
  const fault    = meters.filter((m) => m.status === "fault").length;
  const valveOpen = meters.filter((m) => m.valve_open).length;

  const balanceBarData = balances
    .map((b) => ({ name: b.serial_number.slice(-8), balance: parseFloat(b.balance) }))
    .sort((a, b) => b.balance - a.balance).slice(0, 8);

  const usageAreaData = usageData.map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-KE", { day: "numeric", month: "short" }),
    consumed: parseFloat(d.units_consumed),
  }));

  const flowChartData = dbReadings.map((r) => ({
    time: new Date(r.timestamp).toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" }),
    flow: parseFloat(r.flow_rate),
    pressure: parseFloat(r.pressure_bar),
  }));

  const pieData = [
    { name: "Online",  value: online  },
    { name: "Offline", value: offline },
    { name: "Fault",   value: fault   },
  ].filter((d) => d.value > 0);
  const PIE_COLORS = ["#10b981", "#94a3b8", "#f43f5e"];

  const payStatusColors: Record<string, string> = {
    completed: "text-emerald-600 bg-emerald-50",
    pending:   "text-amber-600 bg-amber-50",
    failed:    "text-rose-600 bg-rose-50",
    cancelled: "text-gray-500 bg-gray-100",
  };

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-KE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  if (loading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-16 w-80" />
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-72 lg:col-span-2" /><Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-black text-gray-900">{greeting()}, {user?.first_name || user?.username} 👋</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{dateStr}</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-100 rounded-2xl px-4 py-2.5 shadow-sm">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
          <span className="text-[13px] font-black text-gray-700">{online} meter{online !== 1 ? "s" : ""} online</span>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard label="Active Meters" value={`${online}/${summary?.total_meters ?? 0}`} sub="Transmitting now" badge="Live" pulse
          gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8} className="w-5 h-5"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" strokeLinecap="round" /></svg>} />
        <KpiCard label="Monthly Revenue" value={`KES ${fmt(summary?.monthly_revenue_kes ?? 0, 0)}`} sub={summary?.month ?? "—"}
          gradient="bg-gradient-to-br from-violet-500 to-purple-700"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8} className="w-5 h-5"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" strokeLinecap="round" /></svg>} />
        <KpiCard label="Token Balance" value={fmt(summary?.total_balance_units ?? 0, 0)} sub="Units across all meters"
          gradient="bg-gradient-to-br from-sky-500 to-blue-600"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8} className="w-5 h-5"><path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" /></svg>} />
        <KpiCard label="Fault / Offline" value={`${fault} / ${offline}`} sub={`${valveOpen} valves open`}
          gradient={fault > 0 ? "bg-gradient-to-br from-rose-500 to-red-600" : "bg-gradient-to-br from-slate-600 to-slate-800"}
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.8} className="w-5 h-5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" strokeLinecap="round" /></svg>} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader title="Revenue & Transactions" sub="Monthly M-Pesa collections" />
          <div className="px-5 pb-5">
            {trends.length === 0 ? (
              <div className="h-52 flex items-center justify-center text-gray-300 text-sm">No payment data yet</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={trends} barCategoryGap="35%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={<ChartTip prefix="KES " />} cursor={{ fill: "#f8fafc" }} />
                    <Bar dataKey="total" name="Revenue" radius={[6, 6, 0, 0]}>
                      {trends.map((_, i) => (
                        <Cell key={i} fill={i === trends.length - 1 ? "#0f2557" : "#bae6fd"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-5 pt-3 border-t border-gray-50 mt-1">
                  <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#0f2557]" /><span className="text-[11.5px] text-gray-400">Current month</span></div>
                  <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#bae6fd]" /><span className="text-[11.5px] text-gray-400">Previous months</span></div>
                </div>
              </>
            )}
          </div>
        </Card>
        <Card className="flex flex-col">
          <CardHeader title="Meter Health" sub="Device status breakdown" />
          <div className="px-5 pb-5 flex-1 flex flex-col">
            {meters.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-gray-300 text-sm">No meters</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={42} outerRadius={62} paddingAngle={4} dataKey="value">
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <Tooltip content={<ChartTip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-2">
                  {[
                    { label: "Online", count: online, color: "bg-emerald-400", pct: Math.round((online / meters.length) * 100) },
                    { label: "Offline", count: offline, color: "bg-slate-300", pct: Math.round((offline / meters.length) * 100) },
                    { label: "Fault", count: fault, color: "bg-rose-400", pct: Math.round((fault / meters.length) * 100) },
                  ].map((row) => (
                    <div key={row.label}>
                      <div className="flex items-center justify-between text-[12px] mb-1">
                        <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${row.color}`} /><span className="text-gray-500">{row.label}</span></div>
                        <span className="font-black text-gray-700">{row.count}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div className={`h-full rounded-full ${row.color} transition-all`} style={{ width: `${row.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Live Flow History" sub={`Last ${flowChartData.length} readings`}
            action={<span className="text-[11px] font-black px-2.5 py-1 rounded-lg text-sky-600 bg-sky-50">L/min</span>} />
          <div className="px-5 pb-5">
            {flowChartData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-gray-300 text-sm">No telemetry yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={flowChartData} margin={{ top: 4, right: 2, left: -22, bottom: 0 }}>
                  <defs>
                    <linearGradient id="mgFlowG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="time" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip suffix=" L/min" />} />
                  <Area type="monotone" dataKey="flow" name="Flow" stroke="#0ea5e9" strokeWidth={2.5}
                    fill="url(#mgFlowG)" dot={false} isAnimationActive={false}
                    activeDot={{ r: 4, fill: "#0ea5e9", stroke: "#fff", strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
        <Card>
          <CardHeader title="Token Balance by Meter" sub="Units remaining per device" />
          <div className="px-5 pb-5">
            {balanceBarData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-gray-300 text-sm">No balances recorded</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={185}>
                  <BarChart data={balanceBarData} layout="vertical" barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={65} />
                    <Tooltip content={<ChartTip suffix=" units" />} cursor={{ fill: "#f8fafc" }} />
                    <Bar dataKey="balance" name="Balance" radius={[0, 6, 6, 0]}>
                      {balanceBarData.map((e, i) => (
                        <Cell key={i} fill={e.balance < 10 ? "#fca5a5" : e.balance < 30 ? "#fde68a" : "#6ee7b7"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-4 pt-3 border-t border-gray-50">
                  {[{ color: "bg-red-300", label: "Critical (<10)" }, { color: "bg-amber-200", label: "Low (<30)" }, { color: "bg-emerald-300", label: "Healthy" }].map((b) => (
                    <div key={b.label} className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-sm ${b.color}`} />
                      <span className="text-[11px] text-gray-400">{b.label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader title="30-Day Consumption" sub={meters[0]?.serial_number ?? "All meters"} />
          <div className="px-5 pb-5">
            {usageAreaData.length === 0 ? (
              <div className="h-48 flex items-center justify-center text-gray-300 text-sm">No usage data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={usageAreaData}>
                  <defs>
                    <linearGradient id="usageGM" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#7c3aed" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTip suffix=" L" />} />
                  <Area type="monotone" dataKey="consumed" name="Consumed" stroke="#7c3aed" strokeWidth={2.5}
                    fill="url(#usageGM)" dot={false} activeDot={{ r: 4, fill: "#7c3aed", stroke: "#fff", strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
        <div className="flex flex-col gap-4">
          <Card className="p-5">
            <CardHeader title="Valve Status" sub="Water flow control" />
            <div className="space-y-3 mt-1">
              {[
                { label: "Flowing", count: valveOpen, color: "bg-sky-50", stroke: "#38bdf8", barColor: "from-sky-400 to-blue-600", pct: meters.length ? (valveOpen / meters.length) * 100 : 0 },
                { label: "Closed", count: meters.length - valveOpen, color: "bg-rose-50", stroke: "#f87171", barColor: "from-rose-400 to-red-500", pct: meters.length ? ((meters.length - valveOpen) / meters.length) * 100 : 0 },
              ].map((row) => (
                <div key={row.label}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg ${row.color} flex items-center justify-center`}>
                        <svg viewBox="0 0 24 24" fill="none" stroke={row.stroke} strokeWidth={1.8} className="w-3.5 h-3.5">
                          <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" />
                        </svg>
                      </div>
                      <span className="text-[12.5px] font-semibold text-gray-600">{row.label}</span>
                    </div>
                    <span className="text-[20px] font-black text-gray-800">{row.count}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className={`h-full rounded-full bg-gradient-to-r ${row.barColor} transition-all`} style={{ width: `${row.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <Card className="flex-1 p-5">
            <CardHeader title="Recent Payments" action={<a href="/payments" className="text-[12px] font-black text-sky-500 hover:text-sky-600">All →</a>} />
            <div className="space-y-2 max-h-[180px] overflow-y-auto mt-1">
              {recentPayments.length === 0 ? (
                <p className="text-[13px] text-gray-300 text-center py-4">No payments</p>
              ) : recentPayments.map((p) => (
                <div key={p.id} className="flex items-center gap-2.5 py-1.5">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${p.status === "completed" ? "bg-emerald-50" : "bg-gray-100"}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke={p.status === "completed" ? "#10b981" : "#9ca3af"} strokeWidth={2} className="w-3.5 h-3.5">
                      <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] font-black text-gray-800">KES {parseFloat(p.amount).toLocaleString()}</p>
                    <p className="text-[10.5px] text-gray-400 truncate">{p.phone_number}</p>
                  </div>
                  <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full capitalize ${payStatusColors[p.status] ?? "text-gray-500 bg-gray-100"}`}>{p.status}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Role router ──────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const { user } = useAuthStore();
  return user?.role === "tenant" ? <TenantDashboard /> : <ManagerDashboard />;
}
